/* eslint-disable no-unsafe-optional-chaining */
/* eslint-disable no-restricted-syntax */
import { useCallback } from 'react';

import APIRoutes from '../vars';

import useChatActions from './useChatActions';
import useStore from '../store';
import { RunEvent, RunResponseContent, type RunResponse } from '../../../types/os';
import { constructEndpointUrl } from '../../../lib/constructEndpointUrl';
import useAIResponseStream from './useAIResponseStream';
import { ToolCall } from '../../../types/os';
import { getJsonMarkdown } from '../../../lib/utils';
import useWorkspaceStore from '../../../store/workspace/workspaceStore';
import { useAuthInfo } from '@propelauth/react';
import { mergeStreamingContent } from '../../../lib/mergeStreamingContent';
import parseToolResult from '../parseToolResult';

const useAIChatStreamHandler = () => {
    const setMessages = useStore((state) => state.setMessages);
    const { addMessage, focusChatInput } = useChatActions();
    const sessionId = useStore((state) => state.sessionId);
    const setSessionId = useStore((state) => state.setSessionId);
    const selectedEndpoint = useStore((state) => state.selectedEndpoint);
    const setStreamingErrorMessage = useStore(
        (state) => state.setStreamingErrorMessage
    );
    const setIsStreaming = useStore((state) => state.setIsStreaming);
    const setSessionsData = useStore((state) => state.setSessionsData);
    const { streamResponse } = useAIResponseStream();
    // Get brainAgentId fresh on each call to ensure we have the latest value
    const getBrainAgentId = useCallback(() => useStore.getState().brainAgentId, []);
    const authInfo: any = useAuthInfo();
    const workspaceId =
        useWorkspaceStore((s) => s.workspace?.id) ||
        (typeof window !== 'undefined' ? localStorage.getItem('he_defws') : null);

    const updateMessagesWithErrorState = useCallback(() => {
        setMessages((prevMessages) => {
            const newMessages = [...prevMessages];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'agent') {
                lastMessage.streamingError = true;
            }
            return newMessages;
        });
    }, [setMessages]);

    /**
     * Processes a new tool call and adds it to the message
     * @param toolCall - The tool call to add (may have new format with tool.name/args)
     * @param prevToolCalls - The previous tool calls array
     * @returns Updated tool calls array
     */
    const processToolCall = useCallback(
        (toolCall: any, prevToolCalls: ToolCall[] = []) => {
            const toolName = toolCall.name || toolCall.tool_name;
            
            // Generate consistent ID or use existing
            // For tools without IDs, use tool name as base (skills-based tools come without IDs)
            let toolCallId = toolCall.tool_call_id || toolCall.id;
            
            // If no ID provided, try to find the most recent incomplete tool with same name
            if (!toolCallId) {
                // Look for most recent tool with same name that doesn't have a result yet
                const incompleteTools = prevToolCalls.filter(
                    tc => tc.tool_name === toolName && (!tc.result || tc.status === 'running')
                );
                
                if (incompleteTools.length > 0) {
                    // Update the most recent incomplete tool
                    toolCallId = incompleteTools[incompleteTools.length - 1].tool_call_id;
                } else {
                    // Generate new ID for new tool call
                    const argsHash = JSON.stringify(toolCall.args || toolCall.tool_args || {});
                    toolCallId = `tool-${toolName}-${prevToolCalls.length}-${argsHash.substring(0, 20)}`;
                }
            }
            
            // Transform new format to legacy format for UI compatibility
            const normalizedToolCall: any = {
                role: toolCall.role || 'tool',
                content: toolCall.content || null,
                tool_call_id: toolCallId,
                tool_name: toolName,
                tool_args: toolCall.args || toolCall.tool_args || {},
                tool_call_error: toolCall.tool_call_error || false,
                metrics: toolCall.metrics || { time: 0 },
                created_at: toolCall.created_at || Math.floor(Date.now() / 1000),
                status: toolCall.status,
                progress: toolCall.progress,
                status_message: toolCall.status_message,
                estimated_time_remaining: toolCall.estimated_time_remaining
            };

            // Parse result if present
            if (toolCall.result !== undefined && toolCall.result !== null) {
                normalizedToolCall.result = parseToolResult(toolCall.result);
            }

            // Find existing tool call by ID
            const existingToolCallIndex = prevToolCalls.findIndex(
                (tc) => tc.tool_call_id === toolCallId
            );

            if (existingToolCallIndex >= 0) {
                const updatedToolCalls = [...prevToolCalls];
                updatedToolCalls[existingToolCallIndex] = {
                    ...updatedToolCalls[existingToolCallIndex],
                    ...normalizedToolCall
                };
                return updatedToolCalls;
            }
            return [...prevToolCalls, normalizedToolCall];
        },
        []
    );

    /**
     * Processes tool calls from a chunk, handling both single tool object and tools array formats
     * @param chunk - The chunk containing tool call data
     * @param existingToolCalls - The existing tool calls array
     * @returns Updated tool calls array
     */
    const processChunkToolCalls = useCallback(
        (
            chunk: RunResponseContent | RunResponse,
            existingToolCalls: ToolCall[] = []
        ) => {
            let updatedToolCalls = [...existingToolCalls];
            // Handle new single tool object format
            if (chunk.tool) {
                updatedToolCalls = processToolCall(chunk.tool, updatedToolCalls);
            }
            // Handle legacy tools array format
            if (chunk.tools && chunk.tools.length > 0) {
                for (const toolCall of chunk.tools) {
                    updatedToolCalls = processToolCall(toolCall, updatedToolCalls);
                }
            }

            return updatedToolCalls;
        },
        [processToolCall]
    );
    const handleStreamResponse = useCallback(
        async (input: string | FormData) => {
            setIsStreaming(true);

            const formData = input instanceof FormData ? input : new FormData();
            if (typeof input === 'string') {
                formData.append('message', input);
            }

            setMessages((prevMessages) => {
                if (prevMessages.length >= 2) {
                    const lastMessage = prevMessages[prevMessages.length - 1];
                    const secondLastMessage = prevMessages[prevMessages.length - 2];
                    if (
                        lastMessage.role === 'agent' &&
                        lastMessage.streamingError &&
                        secondLastMessage.role === 'user'
                    ) {
                        return prevMessages.slice(0, -2);
                    }
                }
                return prevMessages;
            });

            addMessage({
                role: 'user',
                content: formData.get('message') as string,
                created_at: Math.floor(Date.now() / 1000)
            });

            addMessage({
                role: 'agent',
                content: '',
                tool_calls: [],
                streamingError: false,
                created_at: Math.floor(Date.now() / 1000) + 1
            });

            let newSessionId = sessionId;
            try {
                const brainAgentId = getBrainAgentId();
                if (!brainAgentId) {
                    return;
                }

                const endpointUrl = constructEndpointUrl(selectedEndpoint);
                
                // Use skills-based endpoint for model selection support
                const RunUrl = APIRoutes.SkillsStream(endpointUrl);
                
                // Get current model configuration using object destructuring
                const { modelConfig } = useStore.getState();

                // Build headers like in the AgnoProvider context
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',
                };
                if (authInfo?.accessToken) {
                    headers.Authorization = `Bearer ${authInfo.accessToken}`;
                }
                if (authInfo?.user?.userId) {
                    headers['X-User-Id'] = authInfo.user.userId;
                }
                if (workspaceId) {
                    headers['X-Workspace-Id'] = String(workspaceId);
                }
                if (sessionId) {
                    headers['X-Session-Id'] = sessionId;
                }

                // Build JSON request body for skills endpoint
                const requestBody: any = {
                    message: formData.get('message') as string,
                    model_config: {
                        model_key: modelConfig.model_key,
                    },
                };

                // Handle state payload
                const statePayload: Record<string, unknown> = {};
                if (formData.has('state')) {
                    const existingState = formData.get('state');
                    if (typeof existingState === 'string') {
                        try {
                            const parsedState = JSON.parse(existingState);
                            Object.assign(statePayload, parsedState);
                        } catch {
                            // Ignore parsing errors and override with new state payload
                        }
                    }
                }

                if (authInfo?.accessToken) {
                    statePayload.accessToken = authInfo.accessToken;
                }

                if (workspaceId) {
                    statePayload.workspaceId = String(workspaceId);
                }

                if (Object.keys(statePayload).length > 0) {
                    requestBody.state = statePayload;
                }

                // Debug log to verify state payload
                console.log('[AI Chat] Request state:', {
                    hasAccessToken: !!authInfo?.accessToken,
                    workspaceId,
                    statePayload
                });
                
                console.log('[AI Chat] Full request:', {
                    apiUrl: RunUrl,
                    headers: { ...headers, Authorization: headers.Authorization ? '***' : undefined },
                    requestBodyPreview: {
                        ...requestBody,
                        state: requestBody.state ? { ...requestBody.state, accessToken: '***' } : undefined
                    }
                });

                await streamResponse({
                    apiUrl: RunUrl,
                    headers,
                    requestBody,
                    onChunk: (chunk: RunResponse) => {
                        // eslint-disable-next-line no-console
                        console.log('[onChunk]', chunk.event, 'has_connector_draft=', !!(chunk as any).connector_draft);

                        // Capture skills_used from any event that carries it (typically the first)
                        if ((chunk as any).extra_data?.skills_used || (chunk as any).extra?.skills_used) {
                            const skillsUsed = (chunk as any).extra_data?.skills_used || (chunk as any).extra?.skills_used;
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent' && !lastMessage.extra_data?.skills_used) {
                                    lastMessage.extra_data = {
                                        ...lastMessage.extra_data,
                                        skills_used: skillsUsed,
                                    };
                                }
                                return newMessages;
                            });
                        }

                        // Handle new skills-based events
                        if (chunk.event === RunEvent.RunStartedEvent) {
                            newSessionId = chunk.session_id as string;
                            setSessionId(chunk.session_id as string);

                            // Show thinking state with progress tracking
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    lastMessage.isThinking = true;
                                    lastMessage.elapsedMs = chunk.elapsed_ms || 0;
                                    lastMessage.content = '🔍 Starting analysis...';
                                }
                                return newMessages;
                            });

                            if ((!sessionId || sessionId !== chunk.session_id) && chunk.session_id) {
                                const rawName = (formData.get('message') as string) || 'New conversation';
                                const truncatedName = rawName.length > 50 ? rawName.slice(0, 50).replace(/\s+\S*$/, '') + '...' : rawName;
                                const sessionData = {
                                    session_id: chunk.session_id,
                                    session_name: truncatedName,
                                    created_at: chunk.created_at || Date.now()
                                };
                                setSessionsData((prevSessionsData) => {
                                    const sessionExists = prevSessionsData?.some(
                                        (session) => session.session_id === chunk.session_id
                                    );
                                    if (sessionExists) {
                                        return prevSessionsData;
                                    }
                                    return [sessionData, ...(prevSessionsData ?? [])];
                                });
                            }
                        } else if (chunk.event === RunEvent.FirstToken) {
                            // First token received - switch from thinking to streaming
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    lastMessage.isThinking = false;
                                    lastMessage.elapsedMs = chunk.elapsed_ms || lastMessage.elapsedMs || 0;
                                    lastMessage.content = '';
                                }
                                return newMessages;
                            });
                        } else if (chunk.event === RunEvent.ErrorEvent) {
                            updateMessagesWithErrorState();
                            setStreamingErrorMessage(chunk.error || chunk.content as string || 'An error occurred');
                            if (newSessionId) {
                                setSessionsData(
                                    (prevSessionsData: any) =>
                                        prevSessionsData?.filter(
                                            (session: any) => session.session_id !== newSessionId
                                        ) ?? null
                                );
                            }
                        } else if (chunk.event === RunEvent.RunCompletedEvent) {
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    lastMessage.isThinking = false;
                                    lastMessage.elapsedMs = chunk.elapsed_ms || lastMessage.elapsedMs || 0;
                                    // Only update if we have new content
                                    if (chunk.content && typeof chunk.content === 'string' && chunk.content.length > 0) {
                                        lastMessage.content = chunk.content;
                                    }
                                }
                                return newMessages;
                            });
                        } else if ( // Legacy event handlers (keep for backward compatibility)
                            chunk.event === RunEvent.RunStarted ||
                            chunk.event === RunEvent.TeamRunStarted ||
                            chunk.event === RunEvent.ReasoningStarted ||
                            chunk.event === RunEvent.TeamReasoningStarted
                        ) {
                            newSessionId = chunk.session_id as string;
                            setSessionId(chunk.session_id as string);
                            if (
                                (!sessionId || sessionId !== chunk.session_id) &&
                                chunk.session_id
                            ) {
                                const rawName = (formData.get('message') as string) || 'New conversation';
                                const truncatedName = rawName.length > 50 ? rawName.slice(0, 50).replace(/\s+\S*$/, '') + '...' : rawName;
                                const sessionData = {
                                    session_id: chunk.session_id,
                                    session_name: truncatedName,
                                    created_at: chunk.created_at || Date.now()
                                };
                                setSessionsData((prevSessionsData) => {
                                    const sessionExists = prevSessionsData?.some(
                                        (session) => session.session_id === chunk.session_id
                                    );
                                    if (sessionExists) {
                                        return prevSessionsData;
                                    }
                                    return [sessionData, ...(prevSessionsData ?? [])];
                                });
                            }
                        } else if (
                            chunk.event === RunEvent.ToolCallStarted ||
                            chunk.event === RunEvent.TeamToolCallStarted ||
                            chunk.event === RunEvent.ToolCallCompleted ||
                            chunk.event === RunEvent.TeamToolCallCompleted
                        ) {
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    // Handle reasoning content in tool call events
                                    if ((chunk as any).is_reasoning && (chunk as any).reasoning_content) {
                                        const reasoningText = typeof (chunk as any).reasoning_content === 'string' 
                                            ? (chunk as any).reasoning_content 
                                            : '';
                                        lastMessage.thinking = (lastMessage.thinking || '') + reasoningText;
                                    }
                                    
                                    // Set tool status based on event type
                                    const toolWithStatus = chunk.tool ? {
                                        ...chunk.tool,
                                        status: (chunk.event === RunEvent.ToolCallStarted || 
                                                chunk.event === RunEvent.TeamToolCallStarted) 
                                            ? 'running' 
                                            : 'complete'
                                    } : null;

                                    if (toolWithStatus) {
                                        lastMessage.tool_calls = processToolCall(
                                            toolWithStatus,
                                            lastMessage.tool_calls
                                        );
                                    } else {
                                        lastMessage.tool_calls = processChunkToolCalls(
                                            chunk,
                                            lastMessage.tool_calls
                                        );
                                    }
                                    
                                    // Track elapsed time
                                    if (chunk.elapsed_ms) {
                                        lastMessage.elapsedMs = chunk.elapsed_ms;
                                    }
                                }
                                return newMessages;
                            });

                            // When an execute_* tool completes successfully, flip the matching
                            // connectorDraft status from 'pending' → 'sent' so the card updates.
                            if (
                                (chunk.event === RunEvent.ToolCallCompleted ||
                                    chunk.event === RunEvent.TeamToolCallCompleted) &&
                                chunk.tool?.tool_name?.startsWith('execute_') &&
                                chunk.tool?.status !== 'error'
                            ) {
                                const draftId: string | undefined =
                                    (chunk.tool.tool_args as any)?.draft_id;
                                if (draftId) {
                                    setMessages((prev) => {
                                        const updated = [...prev];
                                        let changed = false;
                                        for (let i = 0; i < updated.length; i++) {
                                            const msg = updated[i];
                                            if (msg.role === 'agent') {
                                                // Handle connectorDrafts array
                                                if (msg.connectorDrafts?.some((d) => d.draft_id === draftId)) {
                                                    updated[i] = {
                                                        ...msg,
                                                        connectorDrafts: msg.connectorDrafts.map((d) =>
                                                            d.draft_id === draftId ? { ...d, status: 'sent' } : d
                                                        ),
                                                    };
                                                    changed = true;
                                                    break;
                                                }
                                                // Legacy singleton fallback
                                                if (msg.connectorDraft?.draft_id === draftId) {
                                                    updated[i] = {
                                                        ...msg,
                                                        connectorDraft: { ...msg.connectorDraft!, status: 'sent' }
                                                    };
                                                    changed = true;
                                                    break;
                                                }
                                            }
                                        }
                                        return changed ? updated : prev;
                                    });
                                }
                            }

                            // Route connector_draft onto the matching assistant message.
                            // This runs AFTER processToolCall so the tool_call_id is already
                            // present on the agent message and the primary match can succeed.
                            if ((chunk as any).connector_draft) {
                                const cd = (chunk as any).connector_draft as import('../../../types/os').ConnectorDraft;
                                // eslint-disable-next-line no-console
                                console.log('[connector_draft] received', {
                                    draft_id: cd.draft_id,
                                    provider: cd.provider,
                                    action: cd.action,
                                    tool_call_id: cd.tool_call_id,
                                });
                                setMessages((prev) => {
                                    // Find the latest agent message whose tool_calls contain this tool_call_id
                                    const reversedIdx = [...prev].reverse().findIndex(
                                        (m) => m.role === 'agent' && m.tool_calls?.some(
                                            (tc) => tc.tool_call_id === cd.tool_call_id
                                        )
                                    );
                                    // Fall back to the last agent message if no matching tool_call_id found
                                    const fallbackIdx = [...prev].reverse().findIndex(
                                        (m) => m.role === 'agent'
                                    );
                                    const resolvedReversedIdx = reversedIdx !== -1 ? reversedIdx : fallbackIdx;
                                    // eslint-disable-next-line no-console
                                    console.log('[connector_draft] match', {
                                        matchedByToolCallId: reversedIdx !== -1,
                                        usedFallback: reversedIdx === -1 && fallbackIdx !== -1,
                                        noAgentMessage: resolvedReversedIdx === -1,
                                    });
                                    if (resolvedReversedIdx === -1) return prev;
                                    const realIdx = prev.length - 1 - resolvedReversedIdx;
                                    const updated = [...prev];
                                    const existing = updated[realIdx].connectorDrafts ?? (updated[realIdx].connectorDraft ? [updated[realIdx].connectorDraft!] : []);
                                    const deduped = existing.filter((d) => d.draft_id !== cd.draft_id);
                                    updated[realIdx] = {
                                        ...updated[realIdx],
                                        connectorDrafts: [...deduped, cd],
                                        connectorDraft: undefined, // stop using the singleton field
                                    };
                                    return updated;
                                });
                            }
                        } else if (
                            chunk.event === RunEvent.RunContent ||
                            chunk.event === RunEvent.TeamRunContent
                        ) {
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                
                                // Handle reasoning content separately
                                if (
                                    lastMessage &&
                                    lastMessage.role === 'agent' &&
                                    (chunk as any).is_reasoning &&
                                    (chunk as any).reasoning_content !== undefined
                                ) {
                                    // Append to thinking field for reasoning content
                                    const reasoningText = typeof (chunk as any).reasoning_content === 'string' 
                                        ? (chunk as any).reasoning_content 
                                        : '';
                                    lastMessage.thinking = (lastMessage.thinking || '') + reasoningText;
                                    
                                    // Track elapsed time
                                    lastMessage.elapsedMs = chunk.elapsed_ms || lastMessage.elapsedMs || 0;
                                    
                                    // Still handle tool calls and other metadata
                                    lastMessage.tool_calls = processChunkToolCalls(
                                        chunk,
                                        lastMessage.tool_calls
                                    );
                                    
                                    lastMessage.created_at = chunk.created_at ?? lastMessage.created_at;
                                } else if (
                                    lastMessage &&
                                    lastMessage.role === 'agent' &&
                                    typeof chunk.content === 'string' &&
                                    chunk.content !== undefined
                                ) {
                                    // Handle regular content (not reasoning)
                                    lastMessage.content = mergeStreamingContent(
                                        lastMessage.content || '',
                                        chunk.content
                                    );

                                    // Track elapsed time
                                    lastMessage.elapsedMs = chunk.elapsed_ms || lastMessage.elapsedMs || 0;

                                    // Handle tool calls streaming
                                    lastMessage.tool_calls = processChunkToolCalls(
                                        chunk,
                                        lastMessage.tool_calls
                                    );

                                    // Handle thinking content from skills-based endpoint (legacy)
                                    if ((chunk as any).thinking) {
                                        lastMessage.thinking = (chunk as any).thinking;
                                    }

                                    if (chunk.extra_data?.reasoning_steps) {
                                        lastMessage.extra_data = {
                                            ...lastMessage.extra_data,
                                            reasoning_steps: chunk.extra_data.reasoning_steps
                                        };
                                    }

                                    if (chunk.extra_data?.references) {
                                        lastMessage.extra_data = {
                                            ...lastMessage.extra_data,
                                            references: chunk.extra_data.references
                                        };
                                    }

                                    lastMessage.created_at =
                                        chunk.created_at ?? lastMessage.created_at;
                                    if (chunk.images) {
                                        lastMessage.images = chunk.images;
                                    }
                                    if (chunk.videos) {
                                        lastMessage.videos = chunk.videos;
                                    }
                                    if (chunk.audio) {
                                        lastMessage.audio = chunk.audio;
                                    }
                                } else if (
                                    lastMessage &&
                                    lastMessage.role === 'agent' &&
                                    typeof chunk?.content !== 'string' &&
                                    chunk.content !== null
                                ) {
                                    const jsonBlock = getJsonMarkdown(chunk?.content);
                                    lastMessage.content += jsonBlock;
                                    // Track elapsed time for JSON content too
                                    lastMessage.elapsedMs = chunk.elapsed_ms || lastMessage.elapsedMs || 0;
                                } else if (
                                    chunk.response_audio?.transcript &&
                                    typeof chunk.response_audio?.transcript === 'string'
                                ) {
                                    const { transcript } = chunk.response_audio;
                                    lastMessage.response_audio = {
                                        ...lastMessage.response_audio,
                                        transcript:
                                            lastMessage.response_audio?.transcript + transcript
                                    };
                                }
                                return newMessages;
                            });
                        } else if (
                            chunk.event === RunEvent.ReasoningStep ||
                            chunk.event === RunEvent.TeamReasoningStep
                        ) {
                            setMessages((prevMessages: any) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    const existingSteps =
                                        lastMessage.extra_data?.reasoning_steps ?? [];
                                    const incomingSteps = chunk.extra_data?.reasoning_steps ?? [];
                                    lastMessage.extra_data = {
                                        ...lastMessage.extra_data,
                                        reasoning_steps: [...existingSteps, ...incomingSteps]
                                    };
                                }
                                return newMessages;
                            });
                        } else if (
                            chunk.event === RunEvent.ReasoningCompleted ||
                            chunk.event === RunEvent.TeamReasoningCompleted
                        ) {
                            setMessages((prevMessages) => {
                                const newMessages = [...prevMessages];
                                const lastMessage = newMessages[newMessages.length - 1];
                                if (lastMessage && lastMessage.role === 'agent') {
                                    if (chunk.extra_data?.reasoning_steps) {
                                        lastMessage.extra_data = {
                                            ...lastMessage.extra_data,
                                            reasoning_steps: chunk.extra_data.reasoning_steps
                                        };
                                    }
                                }
                                return newMessages;
                            });
                        } else if (
                            chunk.event === RunEvent.RunError ||
                            chunk.event === RunEvent.TeamRunError ||
                            chunk.event === RunEvent.TeamRunCancelled
                        ) {
                            updateMessagesWithErrorState();
                            const errorContent =
                                (chunk.content as string) ||
                                (chunk.event === RunEvent.TeamRunCancelled
                                    ? 'Run cancelled'
                                    : 'Error during run');
                            setStreamingErrorMessage(errorContent);
                            if (newSessionId) {
                                setSessionsData(
                                    (prevSessionsData: any) =>
                                        prevSessionsData?.filter(
                                            (session: any) => session.session_id !== newSessionId
                                        ) ?? null
                                );
                            }
                        } else if (
                            chunk.event === RunEvent.UpdatingMemory ||
                            chunk.event === RunEvent.TeamMemoryUpdateStarted ||
                            chunk.event === RunEvent.TeamMemoryUpdateCompleted
                        ) {
                            // No-op for now; could surface a lightweight UI indicator in the future
                        } else if (
                            chunk.event === RunEvent.RunCompleted ||
                            chunk.event === RunEvent.TeamRunCompleted
                        ) {
                            setMessages((prevMessages) => {
                                const newMessages = prevMessages.map((message, index) => {
                                    if (
                                        index === prevMessages.length - 1 &&
                                        message.role === 'agent'
                                    ) {
                                        let updatedContent = message.content;
                                        // Only update content if chunk.content is provided and not empty
                                        // This prevents overwriting accumulated streaming content with an empty string from RunCompleted
                                        if (typeof chunk.content === 'string' && chunk.content.length > 0) {
                                            updatedContent = chunk.content;
                                        } else if (chunk.content && typeof chunk.content !== 'string') {
                                            try {
                                                updatedContent = JSON.stringify(chunk.content);
                                            } catch {
                                                // keep existing content
                                            }
                                        }

                                        return {
                                            ...message,
                                            content: updatedContent,
                                            tool_calls: processChunkToolCalls(
                                                chunk,
                                                message.tool_calls
                                            ),
                                            images: chunk.images ?? message.images,
                                            videos: chunk.videos ?? message.videos,
                                            response_audio: chunk.response_audio,
                                            created_at: chunk.created_at ?? message.created_at,
                                            // Explicitly preserve connector draft fields so the
                                            // approval card(s) survive the RunCompleted object rebuild.
                                            connectorDraft: message.connectorDraft,
                                            connectorDrafts: message.connectorDrafts,
                                            extra_data: {
                                                reasoning_steps:
                                                    chunk.extra_data?.reasoning_steps ??
                                                    message.extra_data?.reasoning_steps,
                                                references:
                                                    chunk.extra_data?.references ??
                                                    message.extra_data?.references,
                                                skills_used:
                                                    chunk.extra_data?.skills_used ??
                                                    message.extra_data?.skills_used,
                                            }
                                        };
                                    }
                                    return message;
                                });
                                return newMessages;
                            });
                        }
                    },
                    onError: (error) => {
                        updateMessagesWithErrorState();
                        setStreamingErrorMessage(error.message);
                        if (newSessionId) {
                            setSessionsData(
                                (prevSessionsData: any) =>
                                    prevSessionsData?.filter(
                                        (session: any) => session.session_id !== newSessionId
                                    ) ?? null
                            );
                        }
                    },
                    onComplete: () => { }
                });
            } catch (error) {
                updateMessagesWithErrorState();
                setStreamingErrorMessage(
                    error instanceof Error ? error.message : String(error)
                );
                if (newSessionId) {
                    setSessionsData(
                        (prevSessionsData: any) =>
                            prevSessionsData?.filter(
                                (session: any) => session.session_id !== newSessionId
                            ) ?? null
                    );
                }
            } finally {
                focusChatInput();
                setIsStreaming(false);
            }
        },
        [
            setMessages,
            addMessage,
            updateMessagesWithErrorState,
            selectedEndpoint,
            streamResponse,
            setStreamingErrorMessage,
            setIsStreaming,
            focusChatInput,
            setSessionsData,
            sessionId,
            setSessionId,
            processChunkToolCalls,
            authInfo?.accessToken,
            workspaceId
        ]
    );

    return { handleStreamResponse };
};

export default useAIChatStreamHandler;
